import { useCallback, useEffect, useState } from "react";
import { USERNAME_MAX_LEN } from "../app/state/appUtils";
import {
  apiAddWorkspaceMember,
  apiFetchWorkspaceMembers,
  apiRemoveWorkspaceMember,
  apiUpdateWorkspaceMemberRole,
  type WorkspaceMemberItem,
} from "../lib/authApi";
import { AppIcon } from "./AppIcon";

type WorkspaceMembersPanelProps = {
  workspaceId: string;
  currentUserId: string;
  open: boolean;
  onClose: () => void;
};

function roleLabel(r: WorkspaceMemberItem["role"]): string {
  switch (r) {
    case "owner":
      return "владелец";
    case "admin":
      return "админ";
    default:
      return "участник";
  }
}

export function WorkspaceMembersPanel({
  workspaceId,
  currentUserId,
  open,
  onClose,
}: WorkspaceMembersPanelProps) {
  const [members, setMembers] = useState<WorkspaceMemberItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addUsername, setAddUsername] = useState("");
  const [addRole, setAddRole] = useState<"member" | "admin">("member");
  const [addBusy, setAddBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return apiFetchWorkspaceMembers(workspaceId)
      .then((d) => setMembers(d.members))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const selfRow = members.find((m) => m.user_id === currentUserId);
  const selfRole = selfRow?.role ?? "member";
  const canAdd = selfRole === "owner" || selfRole === "admin";
  const canChangeRoles = selfRole === "owner";

  const canRemoveTarget = useCallback(
    (target: WorkspaceMemberItem) => {
      if (target.role === "owner") return false;
      if (selfRole === "owner") return true;
      if (selfRole === "admin" && target.role === "member") return true;
      return false;
    },
    [selfRole],
  );

  const submitAdd = useCallback(async () => {
    const u = addUsername.trim().toLowerCase();
    if (!u || addBusy) return;
    setAddBusy(true);
    setError(null);
    try {
      const role = selfRole === "owner" ? addRole : "member";
      await apiAddWorkspaceMember(workspaceId, u, role);
      setAddUsername("");
      setAddRole("member");
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAddBusy(false);
    }
  }, [addBusy, addUsername, addRole, load, selfRole, workspaceId]);

  const onRoleChange = useCallback(
    async (userId: string, role: "member" | "admin") => {
      setError(null);
      try {
        await apiUpdateWorkspaceMemberRole(workspaceId, userId, role);
        await load();
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [load, workspaceId],
  );

  const onRemove = useCallback(
    async (userId: string) => {
      if (!window.confirm("Удалить участника из workspace?")) return;
      setError(null);
      try {
        await apiRemoveWorkspaceMember(workspaceId, userId);
        await load();
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [load, workspaceId],
  );

  if (!open) return null;

  return (
    <div
      className="workspace-members-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Участники workspace"
    >
      <button type="button" className="workspace-members-modal__backdrop" onClick={onClose} aria-label="Закрыть" />
      <div className="workspace-members-dialog">
        <div className="workspace-members-dialog__head">
          <span className="workspace-members-dialog__title">// Участники</span>
          <button type="button" className="workspace-members-dialog__close" onClick={onClose}>
            <AppIcon name="x" size={16} />
          </button>
        </div>
        {error ? <p className="workspace-members-dialog__err">{error}</p> : null}
        {canAdd ? (
          <form
            className="workspace-members-dialog__add"
            onSubmit={(e) => {
              e.preventDefault();
              void submitAdd();
            }}
          >
            <input
              type="text"
              className="workspace-members-dialog__input"
              placeholder="username"
              maxLength={USERNAME_MAX_LEN}
              value={addUsername}
              disabled={addBusy}
              onChange={(e) => setAddUsername(e.target.value)}
              autoComplete="off"
            />
            {selfRole === "owner" ? (
              <select
                className="workspace-members-dialog__select"
                value={addRole}
                disabled={addBusy}
                onChange={(e) => setAddRole(e.target.value as "member" | "admin")}
                aria-label="Роль нового участника"
              >
                <option value="member">member</option>
                <option value="admin">admin</option>
              </select>
            ) : null}
            <button type="submit" className="workspace-members-dialog__add-btn" disabled={addBusy || !addUsername.trim()}>
              Добавить
            </button>
          </form>
        ) : null}
        {loading ? (
          <p className="workspace-members-dialog__hint">загрузка…</p>
        ) : (
          <ul className="workspace-members-list">
            {members.map((m) => (
              <li key={m.user_id} className="workspace-members-list__row">
                <span className="workspace-members-list__name" title={m.user_id}>
                  {m.username}
                </span>
                {canChangeRoles && m.role !== "owner" ? (
                  <select
                    className="workspace-members-dialog__select workspace-members-dialog__select--inline"
                    value={m.role === "admin" ? "admin" : "member"}
                    onChange={(e) => void onRoleChange(m.user_id, e.target.value as "member" | "admin")}
                    aria-label={`Роль ${m.username}`}
                  >
                    <option value="member">member</option>
                    <option value="admin">admin</option>
                  </select>
                ) : (
                  <span className="workspace-members-list__role">{roleLabel(m.role)}</span>
                )}
                {canRemoveTarget(m) ? (
                  <button
                    type="button"
                    className="workspace-members-list__remove"
                    onClick={() => void onRemove(m.user_id)}
                  >
                    удалить
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
